import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import { FaDuotoneIconComponent } from '@fortawesome/angular-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

import { DndDirective } from '../directives/dnd';

/**
 * Shared file picker surface: drop files on it, or click / press Enter or
 * Space to open the browser's file dialog. Either way the chosen files come
 * out of `filesSelected`. By default it renders a dashed, centered zone with
 * an optional icon above a heading and hint; `bare` drops that look so a host
 * component can style its own projected content (see `andor-image-upload`).
 */
@Component({
  selector: 'andor-file-drop-zone',
  standalone: true,
  imports: [FaDuotoneIconComponent],
  templateUrl: './file-drop-zone.html',
  styleUrl: './file-drop-zone.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [DndDirective],
  host: {
    role: 'button',
    '[attr.tabindex]': 'disabled() ? -1 : 0',
    '[attr.aria-disabled]': 'disabled()',
    '[attr.aria-label]': 'ariaLabel() || null',
    '[class.file-drop-zone--bare]': 'bare()',
    '(click)': 'open()',
    '(keydown.enter)': 'onKey($event)',
    '(keydown.space)': 'onKey($event)',
  },
})
export class FileDropZone {
  /** `accept` attribute of the file dialog, e.g. `image/png, image/jpeg`. */
  readonly accept = input('');
  readonly multiple = input(false, { transform: booleanAttribute });
  readonly disabled = input(false, { transform: booleanAttribute });
  readonly bare = input(false, { transform: booleanAttribute });
  readonly icon = input<IconDefinition | undefined>(undefined);
  readonly heading = input('');
  readonly hint = input('');
  readonly ariaLabel = input('');

  readonly filesSelected = output<File[]>();

  private readonly _input = viewChild.required<ElementRef<HTMLInputElement>>('input');

  constructor() {
    inject(DndDirective).fileDropped.subscribe((files) => this._emit(files));
  }

  /** Opens the browser's file dialog. */
  open() {
    if (this.disabled()) return;
    this._input().nativeElement.click();
  }

  protected onKey(event: Event) {
    event.preventDefault();
    this.open();
  }

  protected onPicked(input: HTMLInputElement) {
    if (input.files) this._emit(input.files);
    // Reset so picking the same file again still fires `change`.
    input.value = '';
  }

  private _emit(list: FileList) {
    if (this.disabled()) return;
    const files = Array.from(list);
    if (files.length === 0) return;
    this.filesSelected.emit(this.multiple() ? files : files.slice(0, 1));
  }
}
